import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import Button from "@edaz/edazkit/Button/index";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Loader from "@edaz/edazkit/Loader";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import Uploader from "@edaz/edazkit/Uploader";
import { observer } from "mobx-react-lite";
import React from "react";
import { Alert, ButtonToolbar, Col, Form } from "react-bootstrap";
import localizationProvider from "localization/localizationProvider";
import DistributorChargesOrderDifferencesViewModel from "viewModels/timeCharges/distributors/distributorChargesOrderDifferences/distributorChargesOrderDifferencesViewModel";
import { File } from "views/common/file";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import { translateGeneral } from "utils/localizations";

const DistributorChargesOrderDifferencesView: ViewComponent<DistributorChargesOrderDifferencesViewModel> = observer(({ vm }) => {
  let body: React.ReactFragment;

  if (vm.uploading) {
    body = (
      <div className="text-center py-5">
        <div className="d-flex justify-content-center my-4">
          <Loader />
        </div>
        <span className="font-weight-bold text-uppercase">{vm.translate("uploading")}</span>
      </div>
    );
  } else if (vm.response) {
    body = (
      <div className="text-center py-4">
        <Icon className="text-primary mb-4" icon={IconType.CircleCheck} width={64} height={64} />
        <h4 className="mb-4">{vm.translate("complete")}</h4>
        <div className="mb-4">{vm.translate("description")}</div>
        <Button size="sm" className="text-uppercase" onClick={vm.onDownloadClick}>
          {vm.translate("downloadFile")}
        </Button>
        <Button size="sm" className="text-uppercase" variant="light" onClick={vm.onResetClick}>
          {vm.translate("reset")}
        </Button>
      </div>
    );
  } else if (vm.error) {
    body = (
      <div className="text-center py-4">
        <Icon className="text-danger mb-4" icon={IconType.Cross} width={64} height={64} />
        <h4 className="mb-2">{vm.translate("error")}</h4>
        <div className="mb-4">{vm.error}</div>
        <Button size="sm" className="text-uppercase" onClick={vm.onTryAgainClick}>
          {vm.translate("tryAgain")}
        </Button>
      </div>
    );
  } else {
    body = (
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <Form onSubmit={preventDefault(vm.onSubmit)}>
          {vm.distributorHasNoPaymentRequests && (
            <Alert variant="warning">{vm.translate("noDistributorPaymentRequestAvailable")}</Alert>
          )}
          <Form.Row>
            <FormGroup as={Col} target={vm.item} property="distributorId">
              <FormLabel>{vm.translateAttribute("distributorId")}</FormLabel>
              <AdvancedSelect mode="key" items={vm.distributorOptions} />
            </FormGroup>
            <FormGroup as={Col} target={vm.item} property="paymentRequestId">
              <FormLabel>{vm.translateAttribute("paymentRequestId")}</FormLabel>
              <AdvancedSelect mode="key" custom items={vm.distributorPaymentRequestOptions} />
            </FormGroup>
          </Form.Row>
          <Form.Row>
            <div className="col">
              <FormLabel required={true} className={vm.item.file ? "" : "mb-n3"}>
                {vm.translateAttribute("file")}
              </FormLabel>
              {vm.item.file ? (
                <File file={vm.item.file} onRemoveClick={vm.onRemoveFileClick} error={vm.fileError} />
              ) : (
                <Uploader
                  onDrop={vm.onDrop}
                  localization={localizationProvider.uploaderLocalization}
                  label=""
                  accept={[".xlsx"]}
                  error={vm.fileError}
                />
              )}
            </div>
          </Form.Row>
          <ButtonToolbar className="justify-content-center mt-2">
            <Button size="sm" className="text-uppercase" variant="light" onClick={vm.onResetClick}>
              {translateGeneral("reset")}
            </Button>
            <Button size="sm" className="text-uppercase" variant="primary" type="submit">
              {vm.translate("check")}
            </Button>
          </ButtonToolbar>
        </Form>
      </LoaderOverlay>
    );
  }

  return (
    <>
      <h3>{vm.name}</h3>
      {body}
    </>
  );
});

registerView(DistributorChargesOrderDifferencesView, DistributorChargesOrderDifferencesViewModel);
